import { globals } from 'common-ui';
import type { CartResponse, DiscountedPricePerQuantity, IncludedDiscount, LineItem, CartProps } from 'tsconfig/types.d';

type CartTransformInput = CartProps | CartResponse | undefined;

const getDeliveryGroups = (data: CartTransformInput): CartProps['deliveryGroups'] | undefined => {
  if (data && 'deliveryGroups' in data) {
    return data.deliveryGroups;
  }

  return undefined;
};

const transformShippingInfo = (data: CartTransformInput) => ({
  shippingMethodName: data?.shippingInformation?.shippingMethodName ?? '',
  price: {
    amount: data?.shippingInformation?.price?.amount ?? 0,
    currencyCode: data?.shippingInformation?.price?.currencyCode ?? '',
  },
  shippingMethodKey: data?.shippingInformation?.shippingMethodKey ?? '',
});

export const transformAwsCartToVineCart = (vineCartData: CartTransformInput): CartProps => {
  const version = vineCartData?.version ?? 0;
  const id = vineCartData?.id ?? '';
  const totalLineItems = vineCartData?.totalLineItems ?? 0;
  const totalItemQuantity = vineCartData?.totalItemQuantity ?? 0;
  const numberOfBottles = vineCartData?.numberOfBottles ?? 0;
  const responseCode = vineCartData?.responseCode ?? '';
  const totalPrice = {
    amount: vineCartData?.totalPrice?.amount ?? 0,
    currencyCode: vineCartData?.totalPrice?.currencyCode ?? '',
  };
  const totalListPrice = {
    amount: vineCartData?.totalListPrice?.amount ?? 0,
    currencyCode: vineCartData?.totalListPrice?.currencyCode ?? '',
  };

  const lineItemSavings = {
    totalProductDiscountSavings: vineCartData?.lineItemSavings?.totalProductDiscountSavings ?? 0,
    totalCartDiscountSavings: vineCartData?.lineItemSavings?.totalCartDiscountSavings ?? 0,
    totalSavings: vineCartData?.lineItemSavings?.totalSavings ?? 0,
  };

  return {
    version,
    id,
    totalLineItems,
    totalItemQuantity,
    numberOfBottles,
    responseCode,
    lineItems: setLineItems(vineCartData),
    ...(Array.isArray(vineCartData?.voucherCodes) ? { voucherCodes: vineCartData?.voucherCodes } : {}),
    validations: vineCartData?.validations ?? { cartLevel: [] },
    deliveryGroups: getDeliveryGroups(vineCartData),
    totalPrice,
    shippingInformation: transformShippingInfo(vineCartData),
    lineItemSavings,
    totalListPrice,
    ...(vineCartData?.totalPriceDueToday
      ? {
          totalPriceDueToday: {
            amount: vineCartData.totalPriceDueToday.amount,
            currencyCode: vineCartData.totalPriceDueToday.currencyCode,
          },
        }
      : {}),
  };
};

function transformProduct(item: any): LineItem['product'] {
  return {
    itemCode: item?.product?.itemCode ?? '',
    productType: item?.product?.productType ?? '',
    ...(item?.product?.schemeDetails ? { schemeDetails: item.product.schemeDetails } : {}),
    productWebType: '',
    productName: [{ key: '', value: '' }],
    productDescription: [{ key: '', value: '' }],
    productLongDescription: [{ key: '', value: '' }],
    webHeadline: null,
    altShortdescription: '',
    altMediumdescription: '',
    altLongdescription: '',
    appellationRegionDescription: [{ key: '', value: '' }],
    styleDescription: [{ key: '', value: '' }],
    genGrapeDescription: [{ key: '', value: '' }],
    countryRegionDescription: [{ key: '', value: '' }],
    genericRegionDescription: [{ key: '', value: '' }],
    shortTastingNotesColor: '',
    shortTastingNotesAroma: '',
    shortTastingNotesTaste: '',
    averageOverallRating: 0,
    totalReviewCount: 0,
    accolades: [
      {
        accoladeId: '',
        awardingCountry: '',
        accoladeTypeId: '',
        accoladeTypeDescription: '',
        awardTypeId: '',
        awardTypeDescription: '',
        accoladeDate: '',
        accoloadeYear: '',
        accoladeLevelId: '',
        accoladeLevelDescription: '',
        accoladeOrganizationId: '',
        accoladeOrgDescription: '',
        accoladeImageFile: '',
        bestQuoteAuthor: '',
      },
    ],
    itemStemId: '',
    kosher: false,
    vegan: false,
    vegetarian: false,
    awardWinner: false,
    packagePioneer: false,
    biodynamic: false,
    organic: false,
    fairTrade: false,
    oaked: false,
    drinkByDate: '',
    alcoholPercent: 0,
    alcoholUnits: 0,
    allergens: [],
    colourCode: '',
    vintage: item?.product?.vintage ?? '',
    fineWineFlag: false,
    bottleType: item?.product?.bottleType ?? '',
    inventoryInfo: {
      availabilityStatus: item?.product?.inventoryInfo?.availabilityStatus ?? '',
      summaryAvailabilityStatus: item?.product?.inventoryInfo?.summaryAvailabilityStatus ?? '',
      stockQuantity: item?.product?.inventoryInfo?.stockQuantity ?? 0,
      backOrderLevel: item?.product?.inventoryInfo?.backOrderLevel ?? 0,
      happyToWaitEnabled: item?.product?.inventoryInfo?.happyToWaitEnabled ?? false,
      availabilityDate: item?.product?.inventoryInfo?.availabilityDate ?? '',
      stockAlgorithm: item?.product?.inventoryInfo?.stockAlgorithm ?? '',
    },
    mapImageFile: '',
    otherImageFile: '',
    skus: [],
    styleName: '',
    grapeName: '',
    countryName: '',
    colourName: '',
    name: '',
    thumbnailImage: item?.product?.thumbnailImage ?? '',
    smallImage: item?.product?.smallImage ?? '',
    largeImage: item?.product?.largeImage ?? '',
    description: '',
    mixed: item?.product?.mixed,
    isMixed: item?.product?.isMixed,
    enPrimeur: item?.product?.enPrimeurFlag,
    giftFlag: item?.sku?.giftFlag,
    favourited: false,
    longDescription: '',
    appellationName: '',
    subProductType: '',
    salesActivity: '',
    packaging: '',
  };
}

function transformSku(item: any): LineItem['sku'] {
  return {
    productItemCode: item?.sku?.productItemCode ?? item?.sku?.itemCode ?? '',
    productName: [
      {
        value: item?.sku?.productName?.[0]?.value ?? item?.product?.name ?? '',
        key: item?.sku?.productName?.[0]?.key ?? item?.product?.name ?? '',
      },
    ],
    itemCode:
      globals?.country !== 'uk' && item?.lineItemIdentification?.depositInformation?.depositType === 'PRESELL'
        ? item?.lineItemIdentification?.depositInformation?.associatedItemCode
        : item?.sku?.itemCode ?? '',
    numberOfBottles: item?.sku?.numberOfBottles ?? item?.bottleCount ?? 0,
    schemeDetails: item?.sku?.schemeDetails ?? [],
  };
}

function transformUnitPrice(item: any, data: any): LineItem['unitPrice'] {
  const currencyCode = item?.unitPrice?.value?.currencyCode ?? data?.response?.orderPriceInfo?.currencyCode ?? '';

  return {
    value: {
      amount: item?.unitPrice?.value?.amount ?? item?.itemPriceInfo?.amount ?? 0,
      currencyCode,
    },
    discounted: {
      value: {
        amount: item?.unitPrice?.discounted?.value?.amount ?? 0,
        currencyCode: item?.unitPrice?.discounted?.value?.currencyCode ?? '',
      },
      discountReason: item?.unitPrice?.discounted?.discountReason ?? '',
    },
    listPrice: {
      amount: item?.unitPrice?.listPrice?.amount ?? item?.itemPriceInfo?.listPrice ?? 0,
      currencyCode,
    },
    priceType:
      item?.unitPrice?.priceType ??
      item?.itemPriceInfo?.pricingBreakdown?.[item?.itemPriceInfo?.pricingBreakdown?.length - 1]?.adjustmentType ??
      '',
  };
}

function transformSavings(item: any): LineItem['savings'] {
  return {
    totalCartDiscountSavings: item?.savings?.totalCartDiscountSavings ?? 0,
    productDiscountSavings: item?.savings?.productDiscountSavings ?? 0,
    totalProductDiscountSavings: item?.savings?.totalProductDiscountSavings ?? item?.itemPriceInfo?.savings ?? 0,
    totalSavings: item?.savings?.totalSavings ?? 0,
  };
}

function transformItemClassifications(item: any): LineItem['itemClassifications'] {
  return (
    item?.itemClassifications ?? item?.lineItemIdentification?.itemClassifications?.contextClassificationDetails ?? []
  );
}

function transformLineItemIdentification(item: any): LineItem['lineItemIdentification'] {
  return {
    depositInformation: {
      associatedItemCode: item?.lineItemIdentification?.depositInformation?.associatedItemCode ?? '',
      depositType: item?.lineItemIdentification?.depositInformation?.depositType ?? '',
    },
  };
}

function setDiscountedPricePerQuantity(item: any): DiscountedPricePerQuantity[] {
  const discountedPricePerQuantity = item?.discountedPricePerQuantity ?? [{}];
  return discountedPricePerQuantity.map((price: DiscountedPricePerQuantity) => ({
    quantity: price?.quantity || 0,
    discountedPrice: {
      value: {
        amount: price?.discountedPrice?.value?.amount || 0,
        currencyCode: price?.discountedPrice?.value?.currencyCode || '',
      },
      includedDiscounts: (price?.discountedPrice?.includedDiscounts || [{}])?.map((discount: IncludedDiscount) => ({
        discount: {
          typeId: discount?.discount?.typeId || '',
          id: discount?.discount?.id || '',
          name: discount?.discount?.name || '',
        },
        discountedAmount: {
          amount: discount?.discountedAmount?.amount || 0,
          currencyCode: discount?.discountedAmount?.currencyCode || '',
        },
      })),
    },
  }));
}

export function setLineItems(data: any): LineItem[] {
  const lineItems = data?.lineItems ?? data?.response?.lineItems ?? [];

  return lineItems.map((item: any) => {
    const baseItem: LineItem = {
      itemType: item?.itemType ?? '',
      lineItemId: item?.lineItemId ?? item?.id ?? '',
      quantity: item?.quantity ?? data?.response?.numItems ?? 0,
      sku: transformSku(item),
      unitPrice: transformUnitPrice(item, data),
      discountedPricePerQuantity: setDiscountedPricePerQuantity(item),
      totalPrice: {
        amount: item?.totalPrice?.amount ?? 0,
        currencyCode: item?.totalPrice?.currencyCode ?? '',
      },
      savings: transformSavings(item),
      itemClassifications: transformItemClassifications(item),
      ...(item?.classificationDetails ? { classificationDetails: item.classificationDetails } : {}),
      ...(item?.giftCardDetails ? { giftCardDetails: item.giftCardDetails } : {}),
      ...(item?.capabilities ? { capabilities: item.capabilities } : {}),
      ...(item?.giftMessage ? { giftMessage: item.giftMessage } : {}),
    };

    if (!globals.useNewCartAPI) {
      baseItem.lineItemIdentification = transformLineItemIdentification(item);
    }

    if (data?.response) {
      baseItem.product = transformProduct(item);
    }

    return baseItem;
  });
}
